const config = {
  defaultAnimation: {
    duration: 800,
  },
  dragSpeed: 0.8,
}

const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    carousel.on('created', (carousel) => {
      carousel.container.toggleAttribute('data-keen-slider-disabled', false)
      carousel.container.toggleAttribute('initialised', true)
    })
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} arrows
 */
const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, max, min } = carousel.track.details

      if (next) {
        next.disabled = abs === max
      }

      if (previous) {
        previous.disabled = abs === min
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)

    const handleEndReached = () => {
      const { maxIdx, rel } = carousel?.track?.details

      const hasMaxIdx = maxIdx > -1
      const hasRel = rel > -1

      if (!hasMaxIdx || !hasRel) return

      next?.setAttribute('aria-hidden', maxIdx === rel)
    }

    carousel.on('updated', handleEndReached)
    carousel.on('slideChanged', handleEndReached)

    const handleHiddenSlides = () => {
      const hasHiddenSlides = !!carousel?.track?.details?.slides.find(
        (slide) => slide?.portion !== 1,
      )

      next?.setAttribute('aria-hidden', !hasHiddenSlides)

      const options = carousel.options
      options.drag = hasHiddenSlides
      carousel.update(options)
    }

    carousel.on('created', handleHiddenSlides)
    carousel.on('updated', handleHiddenSlides)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  if (!dots) return
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.ariaCurrent = String(isCurrent)
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

const setCurrent = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateCurrentState = () => {
      carousel.slides.forEach((slideEl, index) => {
        slideEl.setAttribute(
          'aria-current',
          String(index === carousel.track.details.rel),
        )
      })
    }

    carousel.on('created', updateCurrentState)
    carousel.on('slideChanged', updateCurrentState)
  }
}

const setSlideAmount = (count) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateCurrentState = () => {
      count.innerText = carousel.track.details.rel + 1
    }

    carousel.on('created', updateCurrentState)
    carousel.on('slideChanged', updateCurrentState)
  }
}

const addWheelControls = (direction) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let touchTimeout = null
    let wheelActive = false
    let position = {}

    carousel?.container?.style?.setProperty(
      direction === 'horizontal'
        ? 'overscroll-behavior-x'
        : 'overscroll-behavior-y',
      'contain',
    )

    const dispatch = (event, name) => {
      position.x -= event.deltaX
      position.y -= event.deltaY
      carousel.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheelStart = (event) => {
      position = {
        x: event.pageX,
        y: event.pageY,
      }
      dispatch(event, 'ksDragStart')
    }

    const wheel = (event) => {
      dispatch(event, 'ksDrag')
    }

    const wheelEnd = (event) => {
      dispatch(event, 'ksDragEnd')
    }

    const eventWheel = (event) => {
      let supportWheel = true
      let earlyReturn = false
      const { details } = carousel?.track

      /* Ensure we don't block vertical scrolling for horizontal carousels */
      if (
        direction === 'horizontal' &&
        (event?.deltaY > 1 || event?.deltaY < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Ensure we don't block horizontal scrolling for vertical carousels */
      if (
        !earlyReturn &&
        direction === 'vertical' &&
        (event?.deltaX > 1 || event?.deltaX < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Prevent overscrolling for none looping carousels */
      if (!earlyReturn && !carousel.options?.loop) {
        const scrollIndex =
          direction === 'horizontal' ? event?.deltaX : event?.deltaY
        if (
          (details.progress <= 0 && scrollIndex < 0) ||
          (details.position >= details.max && scrollIndex > 0)
        ) {
          earlyReturn = true
        }
      }

      /* Prevent default scroll functionality for carousels supporting wheel and scrolling on the correct plane */
      if (supportWheel) {
        event.preventDefault()
      }

      if (earlyReturn) {
        if (touchTimeout && wheelActive) {
          clearTimeout(touchTimeout)
          wheelActive = false
          wheelEnd(event)
        }
        return
      }

      if (!wheelActive) {
        wheelStart(event)
        wheelActive = true
      }
      wheel(event)
      clearTimeout(touchTimeout)
      touchTimeout = setTimeout(() => {
        wheelActive = false
        wheelEnd(event)
      }, 150)
    }

    carousel.on('created', () => {
      carousel.container.addEventListener('wheel', eventWheel, {
        passive: false,
      })
    })
  }
}

const toggleRelatedItem = (relatedItemEls) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateCurrentState = () => {
      relatedItemEls?.forEach((relatedItemEl, index) => {
        relatedItemEl.setAttribute(
          'aria-hidden',
          String(index !== carousel.track.details.rel),
        )
      })
    }

    carousel.on('created', updateCurrentState)
    carousel.on('slideChanged', updateCurrentState)
  }
}

window.BAO.Carousel = {
  config,
  plugins: {
    setInitialised,
    useArrows,
    useDots,
    setCurrent,
    setSlideAmount,
    addWheelControls,
    toggleRelatedItem,
  },
}
